import { countryDetail } from '@/data/select/country';

type BusinessCardRegistrationValues = Partial<
  Pick<
    RegistrationAccountForm,
    'acc_coname' | 'acc_firstName' | 'acc_lastName' | 'acc_jobPosition' | 'acc_MobileCountryCode' | 'acc_mobile'
  >
>;

const cleanValue = (value?: string | null) => value?.trim() || undefined;

const splitMobileNumber = (
  value?: string | null
): Pick<BusinessCardRegistrationValues, 'acc_MobileCountryCode' | 'acc_mobile'> => {
  const trimmedValue = cleanValue(value);
  if (!trimmedValue) return {};

  const compactValue = trimmedValue.replace(/[\s()-]/g, '');
  const countryMatch = [...countryDetail]
    .sort((left, right) => right.countryCode.length - left.countryCode.length)
    .find(({ countryCode }) => compactValue.startsWith(countryCode));

  if (!countryMatch) {
    return { acc_mobile: trimmedValue };
  }

  const localNumber = compactValue.slice(countryMatch.countryCode.length);

  return {
    acc_MobileCountryCode: `${countryMatch.countryCode} (${countryMatch.name})`,
    acc_mobile: localNumber || trimmedValue,
  };
};

export const mapBusinessCardFieldsToRegistration = (fields: BusinessCardOcrFields): BusinessCardRegistrationValues => {
  const values: BusinessCardRegistrationValues = {};
  const directMappings: Array<[keyof BusinessCardRegistrationValues, string | null | undefined]> = [
    ['acc_coname', fields.acc_coname],
    ['acc_firstName', fields.acc_firstName],
    ['acc_lastName', fields.acc_lastName],
    ['acc_jobPosition', fields.acc_jobPosition],
  ];

  directMappings.forEach(([name, value]) => {
    const cleanedValue = cleanValue(value);
    if (cleanedValue) values[name] = cleanedValue;
  });

  return {
    ...values,
    ...splitMobileNumber(fields.acc_mobile),
  };
};

export const mapBusinessCardSelectionToRegistration = (
  selected: BusinessCardSelectedValues
): BusinessCardRegistrationValues => mapBusinessCardFieldsToRegistration({
  acc_coname: selected.acc_coname,
  acc_firstName: selected.acc_firstName,
  acc_lastName: selected.acc_lastName,
  acc_jobPosition: selected.acc_jobPosition,
  acc_mobile: selected.acc_mobile,
});

export const legacyBusinessCardFieldsToCandidates = (fields: BusinessCardOcrFields): BusinessCardCandidates => {
  const candidate = (field: keyof BusinessCardCandidates, value?: string | null): BusinessCardCandidate[] => {
    const cleaned = cleanValue(value);
    return cleaned ? [{ id: `legacy-${field}`, value: cleaned, sourceText: cleaned, sourceLanguage: 'unknown' }] : [];
  };

  return {
    acc_coname: candidate('acc_coname', fields.acc_coname),
    acc_firstName: candidate('acc_firstName', fields.acc_firstName),
    acc_lastName: candidate('acc_lastName', fields.acc_lastName),
    acc_jobPosition: candidate('acc_jobPosition', fields.acc_jobPosition),
    acc_mobile: candidate('acc_mobile', fields.acc_mobile),
    com_email: candidate('com_email', fields.email),
    acc_tel: candidate('acc_tel', fields.acc_tel),
    acc_fax: candidate('acc_fax', fields.acc_fax),
    acc_address: candidate('acc_address', fields.acc_address),
    acc_website: candidate('acc_website', fields.websites?.[0]),
  };
};
